import { useEffect, useId, useRef, useState } from 'react'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import type { EditableWorkspaceRole } from '../../lib/workspaceMemberRoles'

export function MemberRoleSelect({ role, name, actorRole, managerAvailable, disabled = false, onChange }: {
  role: string
  name: string
  actorRole?: string
  managerAvailable: boolean
  disabled?: boolean
  onChange: (role: EditableWorkspaceRole) => Promise<void> | void
}) {
  const [value, setValue] = useState(role)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const busy = useRef(false)
  const errorId = useId()
  useEffect(() => { setValue(role) }, [role])
  const canEdit = ['owner', 'admin'].includes(actorRole || '') && role !== 'owner' && (actorRole === 'owner' || role !== 'admin')
  if (!canEdit) return <span className="capitalize">{role}</span>
  return <div className="w-full min-w-0 space-y-1 sm:w-40">
    <Select value={value} disabled={disabled || saving} onValueChange={async next => {
      if (busy.current || next === value) return
      busy.current = true
      setSaving(true)
      setError('')
      try {
        await onChange(next as EditableWorkspaceRole)
        setValue(next)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to change role')
      } finally {
        busy.current = false
        setSaving(false)
      }
    }}>
      <SelectTrigger aria-label={`Workspace role for ${name}`} aria-invalid={!!error} aria-describedby={error ? errorId : undefined} className="min-h-11 w-full"><SelectValue /></SelectTrigger>
      <SelectContent><SelectGroup>
        <SelectItem value="member">Member</SelectItem>
        <SelectItem value="manager" disabled={!managerAvailable}>Manager</SelectItem>
        {actorRole === 'owner' && <SelectItem value="admin">Admin</SelectItem>}
      </SelectGroup>
        {!managerAvailable && <p className="max-w-56 px-2 pb-2 pt-1 text-xs text-muted-foreground">Manager requires sign-in and a team.</p>}
      </SelectContent>
    </Select>
    {saving && <p role="status" className="text-xs text-muted-foreground">Saving role…</p>}
    {error && <p id={errorId} role="alert" className="break-words text-xs text-destructive">{error}</p>}
  </div>
}
